import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import SubscriptionDialog from '../dashboard/profile/SubscriptionDialog';
import { useWhitelabel } from '../context/WhitelabelContext';
import { useAuth } from '../context/AuthContext';
import { hostname } from '../utils';
import axios from 'axios';
import { isFreeUser } from '../utils/subscriptionUtils';

interface SubscribeProps {
  mode: string;
}

/**
 * Subscription page.
 *
 * - Main platform: shown at the END of onboarding. Onboarding.tsx passes the post-subscribe
 *   destination (the new company's employees page) via router state (`nextPath`). The user can
 *   choose a plan or skip with the free plan to continue into the app.
 * - Whitelabel with disableFreeAccounts=true: shown before onboarding; user must select a paid
 *   plan, then continues to /onboarding (the default `nextPath`).
 *
 * This page:
 * 1. Shows subscription dialog
 * 2. After successful payment or free selection, redirects to `nextPath`
 * 3. Keeps subscription logic isolated from onboarding flow
 */
export default function Subscribe({ mode }: SubscribeProps) {
  const [loading, setLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState('free');
  const { whitelabelSettings } = useWhitelabel();
  const { currentUser, signOut, getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Where to go once the subscription step is done. On the main platform this page is shown at
  // the END of onboarding, so Onboarding.tsx passes the new company's employees page via router
  // state. When it's reached before onboarding (whitelabel disableFreeAccounts) we fall back to
  // /onboarding.
  const nextPath: string = (location.state as { nextPath?: string } | null)?.nextPath || '/onboarding';

  // Determine if a paid subscription is required (whitelabel with free accounts disabled)
  const requiresPaidSubscription = !!(whitelabelSettings?.disableFreeAccounts && !whitelabelSettings?.customSubscriptionEnabled);

  // Check user's subscription status
  const checkSubscription = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const subscriptionType = response.data.usage?.subscriptionType || 'free';
      setCurrentPlan(subscriptionType.toLowerCase());

      // If user already has a paid subscription, skip ahead.
      if (!isFreeUser(subscriptionType)) {
        console.log('[Subscribe] User already has subscription, redirecting to', nextPath);
        navigate(nextPath);
        return;
      }
      setLoading(false);
    } catch (error) {
      console.error('[Subscribe] Error checking subscription:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      checkSubscription();
    } else {
      // Not authenticated, redirect to login
      navigate('/login');
    }
  }, [currentUser, navigate]);

  const handleSubscriptionComplete = () => {
    console.log('[Subscribe] Subscription completed - redirecting to', nextPath);
    // After subscription is complete (or skipped), continue to the next destination.
    navigate(nextPath);
  };

  const handleLogout = async () => {
    try {
      signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (loading || !currentUser) {
    return <LoadingScreen open={true} />;
  }

  return (
    <>
      <SubscriptionDialog
        open={true}
        onClose={handleSubscriptionComplete}
        currentUser={currentUser}
        getUsage={async () => {
          console.log('[Subscribe] getUsage called');
        }}
        getToken={getToken}
        eventListener={(event: any) => {
          console.log('[Subscribe] eventListener called:', event);
          // When payment succeeds, continue to the next destination immediately.
          if (event.type === 'SET_SUCCESS') {
            console.log('[Subscribe] Payment success event - navigating to', nextPath);
            setTimeout(() => {
              navigate(nextPath);
            }, 500);
          }
        }}
        mode={mode}
        currentPlan="free"
        hasExistingSubscription={false}
        currentSubscriptionPlan="free"
        currentSubscriptionConfig={{
          seats: 1,
          companies: 1,
          creditPackages: 0,
        }}
        onSubscriptionUpdate={() => {
          console.log('[Subscribe] onSubscriptionUpdate - subscription completed');
          handleSubscriptionComplete();
        }}
        skipReloadAfterPayment={true}
        requiresSubscription={requiresPaidSubscription}
        onLogout={requiresPaidSubscription ? handleLogout : undefined}
      />
    </>
  );
}
